<script setup>
import { computed } from 'vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

/**
 * Header estándar de página (Masifi) — usar en TODA vista para un header uniforme y
 * compacto. Layout: (back opcional) + título a la izquierda, cluster de acciones a la
 * derecha; búsqueda inline en desktop / fila full-width en móvil; slot `below` para una
 * fila secundaria (tabs, filtros, presets). Componer SIEMPRE con componentes nativos
 * (Button md = 40px) en los slots — sin `<button>` a mano ni overrides de altura inline.
 *
 * Slots:  title (izq) · actions (der, Button md) · below (fila full-width secundaria).
 * Props:  searchPlaceholder (activa el buscador, v-model:search) · showBack (back icon).
 * Emits:  update:search · back (la vista navega).
 */
const props = defineProps({
  searchPlaceholder: { type: String, default: '' },
  search: { type: String, default: '' },
  showBack: { type: Boolean, default: false },
});
const emit = defineEmits(['update:search', 'back']);

const searchModel = computed({
  get: () => props.search,
  set: value => emit('update:search', value),
});
</script>

<template>
  <header
    class="flex flex-col flex-shrink-0 gap-2 px-4 py-2 border-b border-n-weak sm:px-6"
  >
    <div class="flex items-center justify-between gap-2">
      <div class="flex items-center min-w-0 gap-2">
        <Button
          v-if="showBack"
          ghost
          color="slate"
          icon="i-lucide-arrow-left"
          class="flex-shrink-0"
          @click="emit('back')"
        />
        <slot name="title" />
      </div>
      <div class="flex items-center flex-shrink-0 gap-1.5">
        <Input
          v-if="searchPlaceholder"
          v-model="searchModel"
          class="hidden md:block"
          custom-input-class="!mb-0 w-44 lg:w-56"
          :placeholder="searchPlaceholder"
        />
        <slot name="actions" />
      </div>
    </div>

    <!-- Fila secundaria opcional (tabs / filtros / presets), full-width. -->
    <slot name="below" />

    <!-- Búsqueda full-width: solo móvil (en desktop va inline arriba). -->
    <Input
      v-if="searchPlaceholder"
      v-model="searchModel"
      class="md:hidden"
      custom-input-class="!mb-0 w-full"
      :placeholder="searchPlaceholder"
    />
  </header>
</template>
